import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Assignment as InitiativeIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Add as AddIcon,
  Close as CloseIcon,
  AccountTree as GraphIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LinkOff as UnlinkIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  GitHub as GitHubIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { Contact, Initiative, Event, Organization } from '../../types/database';
import { ContactModal } from './ContactModal';
import { InitiativeModal } from '../initiatives/InitiativeModal';
import { ContactRelationships } from './ContactRelationships';
import { ContactEvents } from '../ContactEvents';
import { KnownAssociates } from './KnownAssociates';
import { EventForm } from '../EventForm';
import { ContactEmails } from './ContactEmails';
import { ApiToggleButton } from '../common/ApiToggleButton';
import { ManualNewsSearchButton } from '../common/ManualNewsSearchButton';
import { PeopleDataLookup } from './PeopleDataLookup';
import { format } from 'date-fns';

export const ContactDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const db = useDatabase();
  const [contact, setContact] = useState<Contact | null>(null);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventsRefreshTrigger, setEventsRefreshTrigger] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [linkInitiativeDialogOpen, setLinkInitiativeDialogOpen] = useState(false);
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [newInitiativeModalOpen, setNewInitiativeModalOpen] = useState(false);
  const [showAllInitiatives, setShowAllInitiatives] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventDetailModalOpen, setEventDetailModalOpen] = useState(false);
  const [eventEditModalOpen, setEventEditModalOpen] = useState(false);
  const [linkOrganizationDialogOpen, setLinkOrganizationDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [newInterest, setNewInterest] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load contact
        const { data: contactData, error: contactError } = await db.getContactById(id);
        if (contactError) throw contactError;
        setContact(contactData);

        // Load organizations for this contact
        const { data: contactOrgsData, error: contactOrgsError } = await db.getContactOrganizations(id);
        if (contactOrgsError) {
          console.error('Error loading organizations:', contactOrgsError);
        } else {
          setOrganizations(contactOrgsData || []);
        }

        // Load all initiatives and sort alphabetically
        const { data: initiativesData, error: initiativesError } = await db.getInitiatives();
        if (initiativesError) throw initiativesError;
        const sortedInitiatives = (initiativesData || []).sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setInitiatives(sortedInitiatives);

        // Load all contacts for navigation and sort alphabetically
        const { data: contactsData, error: contactsError } = await db.getContacts();
        if (contactsError) throw contactsError;
        const sortedContacts = (contactsData || []).sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setAllContacts(sortedContacts);

        // Load all organizations and sort alphabetically
        const { data: allOrgsData, error: allOrgsError } = await db.getOrganizations();
        if (allOrgsError) {
          console.error('Error loading organizations:', allOrgsError);
        } else {
          const sortedOrganizations = (allOrgsData || []).sort((a, b) => 
            a.name.localeCompare(b.name)
          );
          setAllOrganizations(sortedOrganizations);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, db]);

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (formData: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'relevance_score'>>, organizationId?: string | null) => {
    if (!contact) return;
    
    try {
      const { error } = await db.updateContact(contact.id, formData);
      if (error) throw error;
      
      // Handle organization linking if provided
      if (organizationId !== undefined) {
        // Get current organizations
        const { data: currentOrgs } = await db.getContactOrganizations(contact.id);
        const currentOrgIds = (currentOrgs || []).map(org => org.id);
        
        // If organizationId is provided and not already linked, link it
        if (organizationId && !currentOrgIds.includes(organizationId)) {
          // Unlink all existing organizations first (or keep them if you want multiple)
          // For now, we'll just link the new one if it's not already linked
          await db.linkContactToOrganization(contact.id, organizationId);
        } else if (organizationId === null && currentOrgIds.length > 0) {
          // If null is explicitly passed and there are existing orgs, unlink all
          // (or you could keep existing ones - this depends on your UX preference)
          // For now, we'll leave existing organizations as-is
        }
      }
      
      // Refresh contact data
      const { data: contactData, error: contactError } = await db.getContactById(contact.id);
      if (contactError) throw contactError;
      setContact(contactData);
      
      setEditModalOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update contact');
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!contact) return;
    
    try {
      const { error } = await db.deleteContact(contact.id);
      if (error) throw error;
      navigate('/contacts');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  };

  const handleLinkInitiative = async () => {
    if (!contact || !selectedInitiative) return;

    try {
      const updatedContacts = [...selectedInitiative.contacts, contact.id];
      const { error } = await db.updateInitiative(selectedInitiative.id, { contacts: updatedContacts });
      if (error) throw error;
      
      // Refresh initiatives data
      const { data: initiativesData, error: initiativesError } = await db.getInitiatives();
      if (initiativesError) throw initiativesError;
      setInitiatives(initiativesData || []);
      
      setLinkInitiativeDialogOpen(false);
      setSelectedInitiative(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link initiative');
    }
  };

  const handleCreateAndLinkInitiative = async (initiativeData: Omit<Initiative, 'id' | 'created_at' | 'updated_at'>) => {
    if (!contact) return;

    try {
      // Create new initiative
      const { data: newInitiative, error: createError } = await db.createInitiative(initiativeData);
      if (createError) throw createError;
      if (!newInitiative) throw new Error('Failed to create initiative');

      // Link the new initiative to the contact
      const updatedContacts = [...newInitiative.contacts, contact.id];
      const { error: linkError } = await db.updateInitiative(newInitiative.id, { contacts: updatedContacts });
      if (linkError) throw linkError;

      // Refresh data
      const { data: initiativesData } = await db.getInitiatives();
      setInitiatives(initiativesData || []);
      
      setNewInitiativeModalOpen(false);
      setLinkInitiativeDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create and link initiative');
    }
  };

  const handleUnlinkInitiative = async (initiativeId: string) => {
    if (!contact) return;

    try {
      const initiative = initiatives.find(i => i.id === initiativeId);
      if (!initiative) return;

      const updatedContacts = initiative.contacts.filter(id => id !== contact.id);
      const { error } = await db.updateInitiative(initiativeId, { contacts: updatedContacts });
      if (error) throw error;
      
      // Refresh initiatives data
      const { data: initiativesData, error: initiativesError } = await db.getInitiatives();
      if (initiativesError) throw initiativesError;
      setInitiatives(initiativesData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink initiative');
    }
  };

  const handleLinkOrganization = async () => {
    if (!contact || !selectedOrganization) return;

    try {
      // Link contact to organization
      const { error } = await db.linkContactToOrganization(contact.id, selectedOrganization.id);
      if (error) throw error;
      
      // Refresh organizations list
      const { data: organizationsData, error: organizationsError } = await db.getContactOrganizations(contact.id);
      if (organizationsError) throw organizationsError;
      setOrganizations(organizationsData || []);
      
      setLinkOrganizationDialogOpen(false);
      setSelectedOrganization(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link organization');
    }
  };

  const handleUnlinkOrganization = async (organizationId: string) => {
    if (!contact) return;

    try {
      // Unlink contact from organization
      const { error } = await db.unlinkContactFromOrganization(contact.id, organizationId);
      if (error) throw error;
      
      // Refresh organizations list
      const { data: organizationsData, error: organizationsError } = await db.getContactOrganizations(contact.id);
      if (organizationsError) throw organizationsError;
      setOrganizations(organizationsData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink organization');
    }
  };

  // Get current contact index and navigation functions
  const currentIndex = contact ? allContacts.findIndex(c => c.id === contact.id) : -1;
  const hasNext = currentIndex < allContacts.length - 1;
  const hasPrevious = currentIndex > 0;

  const handleNext = () => {
    if (hasNext && currentIndex !== -1) {
      const nextContact = allContacts[currentIndex + 1];
      navigate(`/contacts/${nextContact.id}`);
    }
  };

  const handlePrevious = () => {
    if (hasPrevious && currentIndex !== -1) {
      const prevContact = allContacts[currentIndex - 1];
      navigate(`/contacts/${prevContact.id}`);
    }
  };

  const displayedInitiatives = () => {
    if (!contact) return [];
    const linkedInitiatives = initiatives.filter(initiative => initiative.contacts.includes(contact.id));
    return showAllInitiatives ? linkedInitiatives : linkedInitiatives.slice(0, 5);
  };

  const availableInitiatives = contact ? initiatives.filter(initiative => !initiative.contacts.includes(contact.id)) : [];

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setEventDetailModalOpen(true);
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      const { error } = await db.deleteEvent(eventId);
      if (error) throw error;
      
      // Close the modal and refresh events
      setEventDetailModalOpen(false);
      setSelectedEvent(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete event');
    }
  };

  const handleApiToggle = async () => {
    if (!contact) return;
    
    try {
      const newApiState = !contact.use_api_data;
      const { error } = await db.updateContact(contact.id, { use_api_data: newApiState });
      if (error) throw error;
      
      // Refresh contact data from database to ensure consistency
      const { data: updatedContact, error: fetchError } = await db.getContactById(contact.id);
      if (fetchError) throw fetchError;
      
      // Update local state with fresh data
      setContact(updatedContact);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update API data setting');
    }
  };

  const handleAddInterest = async () => {
    if (!contact || !newInterest.trim()) return;
    
    try {
      const currentInterests = contact.interests || [];
      const updatedInterests = [...currentInterests, newInterest.trim()];
      
      const { error } = await db.updateContact(contact.id, { interests: updatedInterests });
      if (error) throw error;
      
      // Refresh contact data
      const { data: contactData, error: contactError } = await db.getContactById(contact.id);
      if (contactError) throw contactError;
      setContact(contactData);
      
      setNewInterest('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add interest');
    }
  };

  const handleRemoveInterest = async (interestToRemove: string) => {
    if (!contact) return;
    
    try {
      const currentInterests = contact.interests || [];
      const updatedInterests = currentInterests.filter(interest => interest !== interestToRemove);
      
      const { error } = await db.updateContact(contact.id, { interests: updatedInterests });
      if (error) throw error;
      
      // Refresh contact data
      const { data: contactData, error: contactError } = await db.getContactById(contact.id);
      if (contactError) throw contactError;
      setContact(contactData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove interest');
    }
  };

  const handleToggleFavorite = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!contact) return;
    
    try {
      const newFavoriteStatus = !contact.is_favorite;
      const { error } = await db.updateContact(contact.id, { is_favorite: newFavoriteStatus });
      if (error) throw error;
      
      // Refresh contact data
      const { data: contactData, error: contactError } = await db.getContactById(contact.id);
      if (contactError) throw contactError;
      setContact(contactData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update favorite status');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  if (!contact) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}>
          <Alert severity="warning">Contact not found</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  // Get linked initiatives
  const linkedInitiatives = initiatives.filter(initiative => initiative.contacts.includes(contact.id));

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        {/* Navigation and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate('/contacts')}>
              <ArrowBackIcon />
            </IconButton>
            <IconButton onClick={handlePrevious} disabled={!hasPrevious}>
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton onClick={handleNext} disabled={!hasNext}>
              <NavigateNextIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleEdit}
              title="Edit Contact"
              sx={{ 
                border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                '& .MuiSvgIcon-root': {
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'black'
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => navigate(`/graph?isolate=${contact.id}`)}
              title="View Graph"
              sx={{ 
                border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                '& .MuiSvgIcon-root': {
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'black'
                }
              }}
            >
              <GraphIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={handleDelete}
              title="Delete Contact"
              sx={{ 
                border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.5)' : 'rgba(211, 47, 47, 0.5)'}`,
                '& .MuiSvgIcon-root': {
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'error.main'
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Contact Information */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{ width: 100, height: 100, mb: 2 }}
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name)}&background=random`}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h5" gutterBottom>
                    {contact.name}
                  </Typography>
                  <IconButton
                    onClick={handleToggleFavorite}
                    size="small"
                    sx={{
                      color: contact.is_favorite ? '#ffc107' : 'text.secondary',
                      '&:hover': {
                        color: '#ffc107',
                      },
                    }}
                    title={contact.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {contact.is_favorite ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                </Box>
                {contact.title && (
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {contact.title}
                  </Typography>
                )}
                {organizations.length > 0 && (
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    {organizations.map(org => org.name).join(', ')}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <List>
                {contact.email && (
                  <ListItem>
                    <ListItemText
                      primary="Email"
                      secondary={contact.email}
                    />
                  </ListItem>
                )}
                {contact.phone && (
                  <ListItem>
                    <ListItemText
                      primary="Phone"
                      secondary={contact.phone}
                    />
                  </ListItem>
                )}
                {contact.industry && (
                  <ListItem>
                    <ListItemText
                      primary="Industry"
                      secondary={contact.industry}
                    />
                  </ListItem>
                )}
                {organizations.length > 0 && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Organizations"
                        secondary={organizations.map(org => org.name).join(', ')}
                      />
                    </ListItem>
                  </>
                )}
                {(contact.linkedin_url || contact.twitter_url || contact.instagram_url || contact.facebook_url || contact.github_url) && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    {contact.linkedin_url && (
                      <ListItem>
                        <ListItemIcon>
                          <LinkedInIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="LinkedIn"
                          secondary={
                            <Box component="a" href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', textDecoration: 'none' }}>
                              {contact.linkedin_url}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                    {contact.twitter_url && (
                      <ListItem>
                        <ListItemIcon>
                          <TwitterIcon sx={{ color: '#1DA1F2' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Twitter/X"
                          secondary={
                            <Box component="a" href={contact.twitter_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1DA1F2', textDecoration: 'none' }}>
                              {contact.twitter_url}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                    {contact.instagram_url && (
                      <ListItem>
                        <ListItemIcon>
                          <InstagramIcon sx={{ color: '#E4405F' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Instagram"
                          secondary={
                            <Box component="a" href={contact.instagram_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#E4405F', textDecoration: 'none' }}>
                              {contact.instagram_url}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                    {contact.facebook_url && (
                      <ListItem>
                        <ListItemIcon>
                          <FacebookIcon sx={{ color: '#1877F2' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Facebook"
                          secondary={
                            <Box component="a" href={contact.facebook_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1877F2', textDecoration: 'none' }}>
                              {contact.facebook_url}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                    {contact.github_url && (
                      <ListItem>
                        <ListItemIcon>
                          <GitHubIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="GitHub"
                          secondary={
                            <Box component="a" href={contact.github_url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.primary', textDecoration: 'none' }}>
                              {contact.github_url}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                  </>
                )}
              </List>

              {contact.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contact.description}
                  </Typography>
                </>
              )}

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                <ApiToggleButton
                  isActive={contact.use_api_data || false}
                  onToggle={handleApiToggle}
                />
                <ManualNewsSearchButton
                  contact={contact}
                  onNewsFound={(count) => {
                    // Trigger events refresh by incrementing the refresh trigger
                    setEventsRefreshTrigger(prev => prev + 1);
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {/* Events Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <ContactEvents 
                    contactId={contact.id} 
                    onEventClick={handleEventClick}
                    refreshTrigger={eventsRefreshTrigger}
                    onEventDeleted={() => {
                      // Trigger refresh when event is deleted
                      setEventsRefreshTrigger(prev => prev + 1);
                    }}
                  />
                </Paper>
              </Grid>

              {/* Initiatives Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Initiatives</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => setLinkInitiativeDialogOpen(true)}
                    >
                      Link Initiative
                    </Button>
                  </Box>

                  <List>
                    {displayedInitiatives().map((initiative) => (
                      <ListItem
                        key={initiative.id}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlinkInitiative(initiative.id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                        onClick={() => navigate(`/initiatives/${initiative.id}`)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: theme => theme.palette.action.hover
                          }
                        }}
                      >
                        <ListItemIcon>
                          <InitiativeIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={initiative.name}
                          secondary={initiative.description}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {linkedInitiatives.length > 5 && (
                    <Button
                      onClick={() => setShowAllInitiatives(!showAllInitiatives)}
                      sx={{ mt: 2 }}
                    >
                      {showAllInitiatives ? 'Show Less' : 'Show More'}
                    </Button>
                  )}
                </Paper>
              </Grid>

              {/* Organization Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Organizations</Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setLinkOrganizationDialogOpen(true)}
                      startIcon={<LinkIcon />}
                      size="small"
                    >
                      Link Organization
                    </Button>
                  </Box>

                  {organizations.length > 0 ? (
                    <List>
                      {organizations.map((org, index) => (
                        <React.Fragment key={org.id}>
                          <ListItem
                            onClick={() => navigate(`/organizations/${org.id}`)}
                            secondaryAction={
                              <IconButton
                                edge="end"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlinkOrganization(org.id);
                                }}
                                title="Unlink from contact"
                                color="error"
                              >
                                <UnlinkIcon />
                              </IconButton>
                            }
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: theme => theme.palette.action.hover
                              }
                            }}
                          >
                            <ListItemIcon>
                              <BusinessIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={org.name}
                              secondary={
                                <Box>
                                  {org.description && (
                                    <Typography variant="body2" color="text.secondary">
                                      {org.description}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                    {org.industry && (
                                      <Chip label={org.industry} size="small" variant="outlined" />
                                    )}
                                    {org.size && (
                                      <Chip label={org.size} size="small" variant="outlined" />
                                    )}
                                    {org.location && (
                                      <Chip label={org.location} size="small" variant="outlined" />
                                    )}
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < organizations.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No organizations linked to this contact
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Interests Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Interests
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      {(contact.interests || []).map((interest, index) => (
                        <Chip
                          key={index}
                          label={interest}
                          onDelete={() => handleRemoveInterest(interest)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {(!contact.interests || contact.interests.length === 0) && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No interests added yet
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Add interest..."
                        value={newInterest}
                        onChange={(e) => setNewInterest(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddInterest();
                          }
                        }}
                        sx={{ flexGrow: 1 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAddInterest}
                        disabled={!newInterest.trim()}
                        startIcon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Relationships Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <ContactRelationships 
                    contact={contact} 
                    onUpdate={(updatedContact) => {
                      setContact(updatedContact);
                      // Refresh all contacts to update the list
                      const loadAllContacts = async () => {
                        const { data: contactsData } = await db.getContacts();
                        setAllContacts(contactsData || []);
                      };
                      loadAllContacts();
                    }}
                  />
                </Paper>
              </Grid>

              {/* PeopleData Labs Lookup Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <PeopleDataLookup
                    contact={contact}
                    onContactUpdate={async () => {
                      // Reload contact data
                      const { data: contactData, error: contactError } = await db.getContactById(id!);
                      if (!contactError && contactData) {
                        setContact(contactData);
                      }
                    }}
                  />
                </Paper>
              </Grid>

              {/* Known Associates Section */}
              <Grid item xs={12}>
                <KnownAssociates
                  contactId={contact.id}
                  contactName={contact.name}
                  contactTitle={contact.title}
                  contactCompany={organizations.length > 0 ? organizations.map(org => org.name).join(', ') : undefined}
                  contactIndustry={contact.industry}
                />
              </Grid>

              {/* Email History Section */}
              <Grid item xs={12}>
                <ContactEmails
                  contactId={contact.id}
                  contactEmail={contact.email}
                  contactName={contact.name}
                  onComposeEmail={(to) => {
                    // This will be handled by the Gmail integration
                    console.log('Compose email to:', to);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Modals and Dialogs */}
      <ContactModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        contact={contact}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {contact?.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={linkInitiativeDialogOpen}
        onClose={() => setLinkInitiativeDialogOpen(false)}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Link Initiative</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Search and select an initiative to link to this contact
              </Typography>
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setNewInitiativeModalOpen(true)}
              variant="outlined"
            >
              Create New Initiative
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            options={availableInitiatives}
            getOptionLabel={(option) => option.name}
            value={selectedInitiative}
            onChange={(_, newValue) => setSelectedInitiative(newValue)}
            renderInput={(params) => <TextField {...params} label="Select Initiative" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkInitiativeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleLinkInitiative}
            disabled={!selectedInitiative}
          >
            Link
          </Button>
        </DialogActions>
      </Dialog>

      <InitiativeModal
        open={newInitiativeModalOpen}
        onClose={() => setNewInitiativeModalOpen(false)}
        onSubmit={handleCreateAndLinkInitiative}
      />

      <Dialog
        open={linkOrganizationDialogOpen}
        onClose={() => setLinkOrganizationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Link Organization</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Search and select an organization to link to this contact
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={allOrganizations.filter(org => !organizations.some(o => o.id === org.id))}
              getOptionLabel={(option) => option.name}
              value={selectedOrganization}
              onChange={(_, newValue) => setSelectedOrganization(newValue)}
              renderInput={(params) => <TextField {...params} label="Select Organization" />}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.description && (
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      {option.industry && (
                        <Chip label={option.industry} size="small" variant="outlined" />
                      )}
                      {option.size && (
                        <Chip label={option.size} size="small" variant="outlined" />
                      )}
                      {option.location && (
                        <Chip label={option.location} size="small" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            />
            
            {selectedOrganization && (
              <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Selected Organization Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography>{selectedOrganization.name}</Typography>
                  </Grid>
                  {selectedOrganization.industry && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Industry
                      </Typography>
                      <Typography>{selectedOrganization.industry}</Typography>
                    </Grid>
                  )}
                  {selectedOrganization.size && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Size
                      </Typography>
                      <Typography>{selectedOrganization.size}</Typography>
                    </Grid>
                  )}
                  {selectedOrganization.location && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography>{selectedOrganization.location}</Typography>
                    </Grid>
                  )}
                  {selectedOrganization.website && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Website
                      </Typography>
                      <Typography>
                        <a 
                          href={selectedOrganization.website.startsWith('http') ? selectedOrganization.website : `https://${selectedOrganization.website}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: 'inherit' }}
                        >
                          {selectedOrganization.website}
                        </a>
                      </Typography>
                    </Grid>
                  )}
                  {selectedOrganization.description && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography>{selectedOrganization.description}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOrganizationDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleLinkOrganization}
            disabled={!selectedOrganization}
            variant="contained"
          >
            Link Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Detail Modal */}
      <Dialog
        open={eventDetailModalOpen}
        onClose={() => setEventDetailModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{selectedEvent.title}</Typography>
                <Box>
                  <IconButton 
                    onClick={() => {
                      setEventDetailModalOpen(false);
                      setEventEditModalOpen(true);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                    color="error"
                    sx={{ mr: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton onClick={() => setEventDetailModalOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                {selectedEvent.description && (
                  <Typography variant="body1" paragraph>
                    {selectedEvent.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Type
                    </Typography>
                    <Typography>
                      {selectedEvent.metadata?.type ? 
                        selectedEvent.metadata.type.charAt(0).toUpperCase() + 
                        selectedEvent.metadata.type.slice(1).replace('_', ' ') :
                        'Unknown'}
                    </Typography>
                  </Box>

                  {selectedEvent.metadata?.tweet_url && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tweet
                      </Typography>
                      <Typography>
                        <a href={selectedEvent.metadata?.tweet_url} target="_blank" rel="noopener noreferrer">
                          View Tweet
                        </a>
                      </Typography>
                    </Box>
                  )}

                  {selectedEvent.metadata?.article_url && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Article
                      </Typography>
                      <Typography>
                        <a href={selectedEvent.metadata?.article_url} target="_blank" rel="noopener noreferrer">
                          Read Article
                        </a>
                      </Typography>
                    </Box>
                  )}

                  {selectedEvent.metadata?.sentiment && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Sentiment
                      </Typography>
                      <Typography>
                        {selectedEvent.metadata?.sentiment?.charAt(0).toUpperCase() + 
                         selectedEvent.metadata?.sentiment?.slice(1)}
                      </Typography>
                    </Box>
                  )}

                  {selectedEvent.metadata?.notes && selectedEvent.metadata.notes.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Notes
                      </Typography>
                      {selectedEvent.metadata.notes.map((note, index) => (
                        <Typography key={index} paragraph>
                          {note}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {selectedEvent.metadata?.tags && selectedEvent.metadata.tags.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tags
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedEvent.metadata.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ my: 1 }} />

                  {/* Optional Date Information */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Optional Date Information
                    </Typography>
                    {selectedEvent.metadata?.start_date && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Start Date
                        </Typography>
                        <Typography>
                          {format(new Date(selectedEvent.metadata?.start_date), 'MMM d, yyyy h:mm a')}
                        </Typography>
                      </Box>
                    )}
                    {selectedEvent.metadata?.end_date && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          End Date
                        </Typography>
                        <Typography>
                          {format(new Date(selectedEvent.metadata?.end_date), 'MMM d, yyyy h:mm a')}
                        </Typography>
                      </Box>
                    )}
                    {!selectedEvent.metadata?.start_date && !selectedEvent.metadata?.end_date && (
                      <Typography variant="body2" color="text.secondary">
                        No dates specified
                      </Typography>
                    )}
                  </Box>

                  {selectedEvent.metadata?.location && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography>📍 {selectedEvent.metadata?.location}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Edit Event Modal */}
      <Dialog
        open={eventEditModalOpen}
        onClose={() => setEventEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <EventForm
              contactId={selectedEvent.contact_id}
              eventId={selectedEvent.id}
              onSuccess={() => {
                setEventEditModalOpen(false);
                setEventsRefreshTrigger(prev => prev + 1);
                setSelectedEvent(null);
              }}
              onCancel={() => setEventEditModalOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};